import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiUser, FiFileText } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { cargoService } from '../../services/api';
import './CadastroCargo.css';

const CadastroCargo = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ativo: true
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome do cargo é obrigatório';
    } else if (formData.nome.trim().length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = 'Nome deve ter no máximo 100 caracteres';
    }

    if (formData.descricao && formData.descricao.length > 500) {
      newErrors.descricao = 'Descrição deve ter no máximo 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário');
      return;
    }

    setLoading(true);
    try {
      const response = await cargoService.create(formData);
      
      if (response.success) {
        toast.success('Cargo cadastrado com sucesso!');
        navigate('/cargos');
      } else {
        toast.error(response.message || 'Erro ao cadastrar cargo');
      }
    } catch (error) {
      console.error('Erro ao cadastrar cargo:', error);
      toast.error('Erro ao cadastrar cargo');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (formData.nome || formData.descricao) {
      if (window.confirm('Tem certeza que deseja cancelar? Os dados não salvos serão perdidos.')) {
        navigate('/cargos');
      }
    } else {
      navigate('/cargos');
    }
  };

  return (
    <div className="cadastro-cargo-page">
      <div className="page-header">
        <div className="header-content">
          <button 
            className="btn-back"
            onClick={() => navigate('/cargos')}
            type="button"
          >
            <FiArrowLeft />
          </button>
          <div>
            <h1>
              <FiUser className="page-icon" />
              Cadastrar Novo Cargo
            </h1>
            <p>Preencha as informações do novo cargo</p>
          </div>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="cargo-form">
          <div className="form-section">
            <h3>Informações Básicas</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nome" className="required">
                  <FiUser /> Nome do Cargo
                </label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  placeholder="Ex: Ancião, Servo Ministerial, Pioneiro..."
                  className={errors.nome ? 'error' : ''}
                  maxLength={100}
                  required
                />
                {errors.nome && (
                  <span className="error-message">{errors.nome}</span>
                )}
                <small className="field-hint">
                  {formData.nome.length}/100 caracteres
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="descricao">
                  <FiFileText /> Descrição
                </label>
                <textarea
                  id="descricao"
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  placeholder="Descreva as responsabilidades e características deste cargo..."
                  rows={4}
                  className={errors.descricao ? 'error' : ''}
                  maxLength={500}
                />
                {errors.descricao && (
                  <span className="error-message">{errors.descricao}</span>
                )}
                <small className="field-hint">
                  {formData.descricao.length}/500 caracteres (opcional)
                </small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="ativo"
                    name="ativo"
                    checked={formData.ativo}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="ativo" className="checkbox-label">
                    Cargo ativo
                    <small>Cargos ativos podem ser atribuídos aos colaboradores</small>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  Salvando...
                </>
              ) : (
                <>
                  <FiSave /> Cadastrar Cargo
                </>
              )}
            </button>
          </div>
        </form>

        <div className="form-sidebar">
          <div className="info-card">
            <h4>💡 Dicas para Cadastro</h4>
            <ul>
              <li><strong>Nome:</strong> Use nomes claros e específicos</li>
              <li><strong>Descrição:</strong> Detalhe as responsabilidades do cargo</li>
              <li><strong>Status:</strong> Cargos inativos não aparecerão nas opções de seleção</li>
            </ul>
          </div>

          <div className="info-card">
            <h4>📋 Exemplos de Cargos</h4>
            <div className="examples">
              <div className="example-item">
                <strong>Ancião</strong>
                <p>Responsável por supervisionar a congregação</p>
              </div>
              <div className="example-item">
                <strong>Servo Ministerial</strong>
                <p>Auxilia os anciãos nas atividades da congregação</p>
              </div>
              <div className="example-item">
                <strong>Pioneiro Regular</strong>
                <p>Dedica tempo integral à pregação</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastroCargo; 